import { MARSHALL_PALMER_SCALE } from './nowcastingConstants';

export interface ColormapEntry {
  maxValue: number;
  label: string;
  color: string;
}

export interface NumericColormap {
  id: string;
  name: string;
  scale: ColormapEntry[];
}

const VIRIDIS_SCALE: ColormapEntry[] = [
  { maxValue: 0.1,  label: '0.1',  color: '#440154' },
  { maxValue: 0.2,  label: '0.2',  color: '#482878' },
  { maxValue: 0.5,  label: '0.5',  color: '#3E4989' },
  { maxValue: 1,    label: '1',    color: '#31688E' },
  { maxValue: 2,    label: '2',    color: '#26828E' },
  { maxValue: 3,    label: '3',    color: '#1F9E89' },
  { maxValue: 6,    label: '6',    color: '#35B779' },
  { maxValue: 10,   label: '10',   color: '#6ECE58' },
  { maxValue: 15,   label: '15',   color: '#B5DE2B' },
  { maxValue: 25,   label: '25',   color: '#FDE725' },
  { maxValue: 50,   label: '50',   color: '#FDE725' },
];

const TURBO_SCALE: ColormapEntry[] = [
  { maxValue: 0.1,  label: '0.1',  color: '#30123B' },
  { maxValue: 0.2,  label: '0.2',  color: '#4145AB' },
  { maxValue: 0.5,  label: '0.5',  color: '#4675ED' },
  { maxValue: 1,    label: '1',    color: '#39A2FC' },
  { maxValue: 2,    label: '2',    color: '#1BD0D5' },
  { maxValue: 3,    label: '3',    color: '#24F17A' },
  { maxValue: 6,    label: '6',    color: '#7CF430' },
  { maxValue: 10,   label: '10',   color: '#C8E814' },
  { maxValue: 15,   label: '15',   color: '#F1B019' },
  { maxValue: 25,   label: '25',   color: '#F86719' },
  { maxValue: 50,   label: '50',   color: '#7A0403' },
];

const HIGH_CONTRAST_SCALE: ColormapEntry[] = [
  { maxValue: 0.5,  label: '0.5',  color: '#87CEEB' },
  { maxValue: 1,    label: '1',    color: '#0066CC' },
  { maxValue: 3,    label: '3',    color: '#00CC00' },
  { maxValue: 6,    label: '6',    color: '#FFFF00' },
  { maxValue: 10,   label: '10',   color: '#FF8800' },
  { maxValue: 25,   label: '25',   color: '#FF0000' },
  { maxValue: 50,   label: '50',   color: '#880088' },
];

// Marshall-Palmer hybrid bands — finer 0.5 mm/h steps in the 1–5 mm/h range,
// with extra bands at 3.5 and 4.5 mm/h. Colors sampled from the reference
// radar visualization colorbar.
const MARSHALL_PALMER_HYBRID_BANDS_SCALE: ColormapEntry[] = [
  { maxValue: 0.25, label: '0.25', color: '#460D7F' },
  { maxValue: 0.5,  label: '0.5',  color: '#8C1ED0' },
  { maxValue: 1,    label: '1',    color: '#D5A1DB' },
  { maxValue: 1.5,  label: '1.5',  color: '#70FDFD' },
  { maxValue: 2,    label: '2',    color: '#7093E6' },
  { maxValue: 2.5,  label: '2.5',  color: '#307C0C' },
  { maxValue: 3,    label: '3',    color: '#5FCB3A' },
  { maxValue: 3.5,  label: '3.5',  color: '#FDFD35' },
  { maxValue: 4,    label: '4',    color: '#F7D630' },
  { maxValue: 4.5,  label: '4.5',  color: '#F1A52B' },
  { maxValue: 5,    label: '5',    color: '#EB8B28' },
  { maxValue: 10,   label: '10',   color: '#E41E22' },
  { maxValue: 50,   label: '50',   color: '#A21215' },
];

const TOMORROW_IO_SCALE: ColormapEntry[] = [
  { maxValue: 0.237, label: '0.2',  color: '#95dd84' },
  { maxValue: 0.365, label: '0.4',  color: '#95dd84' },
  { maxValue: 0.562, label: '0.6',  color: '#6fff00' },
  { maxValue: 0.865, label: '0.9',  color: '#5add03' },
  { maxValue: 1.332, label: '1.3',  color: '#45bc03' },
  { maxValue: 2.05,  label: '2',    color: '#339b02' },
  { maxValue: 3.158, label: '3',    color: '#217c00' },
  { maxValue: 4.862, label: '5',    color: '#fffb00' },
  { maxValue: 7.488, label: '7',    color: '#ffe700' },
  { maxValue: 11.531,label: '12',   color: '#ffb700' },
  { maxValue: 17.756,label: '18',   color: '#ff8900' },
  { maxValue: 27.344,label: '27',   color: '#ff4d00' },
  { maxValue: 42.107,label: '42',   color: '#dd2f04' },
  { maxValue: 50,    label: '50',   color: '#bb0505' },
];

export const NUMERIC_COLORMAPS: NumericColormap[] = [
  { id: 'marshall-palmer',              name: 'Marshall-Palmer',              scale: MARSHALL_PALMER_SCALE },
  { id: 'marshall-palmer-hybrid-bands', name: 'Marshall-Palmer Hybrid Bands', scale: MARSHALL_PALMER_HYBRID_BANDS_SCALE },
  { id: 'tomorrow-io',                  name: 'Tomorrow.io',                  scale: TOMORROW_IO_SCALE },
  { id: 'viridis',         name: 'Viridis',         scale: VIRIDIS_SCALE },
  { id: 'turbo',           name: 'Turbo',           scale: TURBO_SCALE },
  { id: 'high-contrast',   name: 'High Contrast',   scale: HIGH_CONTRAST_SCALE },
];

export function getColormapById(id: string): NumericColormap {
  return NUMERIC_COLORMAPS.find((c) => c.id === id) || NUMERIC_COLORMAPS[0];
}
